/**
 * cl-collection.js — collection page engine for the lookbook collection.
 *
 * <ss-collection data-section-id="…"> progressively enhances sort / filter / reset so they
 * update the grid in place via the Section Rendering API (no full reload). Also toggles the
 * sort dropdown. Drawer open/close is handled separately by cl-filter-drawer.js.
 *
 * Everything is event-delegated on the element, so swapping inner DOM never breaks handlers.
 * If JS is unavailable or a request fails, the underlying links/form still work (full reload).
 */
(function () {
  if (customElements.get('ss-collection')) return;

  class CLCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);

      this.classList.add('is-enhanced'); // hides the no-JS numbered pagination
      this.initInfiniteScroll();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('popstate', this.onPopState);
      if (this.io) this.io.disconnect();
    }

    /* ---------------- Sort dropdown open/close ---------------- */
    get sortRoot() {
      return this.querySelector('.cl-sort');
    }

    toggleSort(force) {
      var root = this.sortRoot;
      if (!root) return;
      var open = force != null ? force : !root.classList.contains('is-open');
      root.classList.toggle('is-open', open);
      var trigger = root.querySelector('[data-sort-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    onDocClick(e) {
      var root = this.sortRoot;
      if (root && root.classList.contains('is-open') && !root.contains(e.target)) {
        this.toggleSort(false);
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape') this.toggleSort(false);
    }

    /* ---------------- Delegated interactions ---------------- */
    onClick(e) {
      var sortTrigger = e.target.closest('[data-sort-trigger]');
      if (sortTrigger && this.contains(sortTrigger)) {
        e.preventDefault();
        this.toggleSort();
        return;
      }

      var sortOption = e.target.closest('a.sort-option');
      if (sortOption && this.contains(sortOption)) {
        if (!this.sectionId) return; // no enhancement → let the link navigate
        e.preventDefault();
        var params = new URLSearchParams(window.location.search);
        params.set('sort_by', sortOption.dataset.sortValue);
        params.delete('page'); // a new sort starts from page 1
        this.toggleSort(false);
        this.navigate(window.location.pathname + '?' + params.toString());
        return;
      }

      var reset = e.target.closest('a.form-reset-btn');
      if (reset && this.contains(reset)) {
        if (!this.sectionId) return;
        e.preventDefault();
        this.navigate(reset.getAttribute('href'));
        return;
      }
    }

    onSubmit(e) {
      var form = e.target.closest('.cl-filter-form');
      if (!form || !this.contains(form)) return;
      if (!this.sectionId) return; // no enhancement → native GET submit (full reload)
      e.preventDefault();

      var params = new URLSearchParams();
      new FormData(form).forEach(function (value, key) {
        if (value !== '') params.append(key, value); // drop empty price inputs
      });
      var action = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
      this.closeDrawer();
      this.navigate(action.pathname + '?' + params.toString());
    }

    /* ---------------- AJAX navigate + swap ---------------- */
    navigate(url) {
      var target = new URL(url, window.location.origin);
      var fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      this.setLoading(true);
      var self = this;
      fetch(fetchUrl.toString())
        .then(function (r) {
          if (!r.ok) throw new Error('Section render failed: ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          self.swap(doc);
          window.history.pushState({ clCollection: true }, '', target.toString());
          self.setLoading(false);
        })
        .catch(function () {
          window.location.assign(target.toString()); // graceful fallback
        });
    }

    onPopState() {
      if (!this.sectionId) return;
      var target = new URL(window.location.href);
      var fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      this.setLoading(true);
      var self = this;
      fetch(fetchUrl.toString())
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          self.swap(doc);
          self.setLoading(false);
        })
        .catch(function () { window.location.reload(); });
    }

    swap(doc) {
      // Product grid (+ lookbook placement + pagination)
      this.replaceEl(doc, '.cl-grid-section');
      // Filter form: hidden view/sort inputs, pill state, price, reset button (all delegated/no listeners)
      this.replaceEl(doc, '.cl-filter-form');
      // Sort control: trigger label (current sort) + dropdown active option. Re-rendered closed.
      this.replaceEl(doc, '.cl-sort');
      // Filter trigger count + underline — updated IN PLACE so the drawer-open listener survives.
      var newTrigger = doc.querySelector('.filter-cta');
      var curTrigger = this.querySelector('.filter-cta');
      if (newTrigger && curTrigger) {
        curTrigger.className = newTrigger.className;
        var nl = newTrigger.querySelector('.filter-cta__label');
        var cl = curTrigger.querySelector('.filter-cta__label');
        if (nl && cl) cl.textContent = nl.textContent;
      }

      // Filter/sort/reset re-renders page 1 → reset infinite scroll onto the fresh grid.
      this.initInfiniteScroll();
    }

    replaceEl(doc, selector) {
      var fresh = doc.querySelector(selector);
      var cur = this.querySelector(selector);
      if (fresh && cur) cur.replaceWith(fresh);
    }

    setLoading(state) {
      var grid = this.querySelector('.cl-grid-section');
      if (grid) grid.classList.toggle('is-loading', !!state);
      this.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    closeDrawer() {
      document.dispatchEvent(new CustomEvent('cl-filter-drawer:close'));
    }

    /* ---------------- Infinite scroll (auto, next-only) ---------------- */
    get grid() { return this.querySelector('.cl-grid'); }
    get sentinel() { return this.querySelector('[data-cl-sentinel]'); }
    get status() { return this.querySelector('[data-cl-status]'); }

    initInfiniteScroll() {
      if (this.io) { this.io.disconnect(); this.io = null; }
      this.loadingPage = false;

      var grid = this.grid;
      if (!grid || !this.sectionId) return;

      this.page = parseInt(grid.dataset.currentPage, 10) || 1;
      this.lastPage = parseInt(grid.dataset.lastPage, 10) || 1;

      var sentinel = this.sentinel;
      if (!sentinel || this.page >= this.lastPage) return;

      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) self.loadNextPage();
        });
      }, { rootMargin: '300px' });
      this.io.observe(sentinel);
    }

    loadNextPage() {
      if (this.loadingPage || this.page >= this.lastPage) return;
      this.loadingPage = true;

      var nextPage = this.page + 1;
      var url = new URL(window.location.href); // keeps current filters + sort + view
      url.searchParams.set('page', String(nextPage));
      url.searchParams.set('section_id', this.sectionId);

      var self = this;
      fetch(url.toString())
        .then(function (r) {
          if (!r.ok) throw new Error('Page fetch failed: ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newItems = doc.querySelectorAll('.cl-grid > .cl-grid__item');
          var grid = self.grid;
          if (grid && newItems.length) {
            grid.append.apply(grid, Array.prototype.slice.call(newItems));
            self.announce(newItems.length);
          }
          self.page = nextPage;
          self.loadingPage = false;
          if (self.page >= self.lastPage) self.teardownInfiniteScroll();
        })
        .catch(function () {
          // Stop enhancing and reveal the numbered pagination fallback.
          self.loadingPage = false;
          self.teardownInfiniteScroll();
          self.classList.remove('is-enhanced');
        });
    }

    teardownInfiniteScroll() {
      if (this.io) { this.io.disconnect(); this.io = null; }
      var s = this.sentinel;
      if (s) s.remove();
    }

    announce(count) {
      if (this.status && count) this.status.textContent = count + ' more products loaded';
    }
  }

  customElements.define('ss-collection', CLCollection);
})();
